/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/PhaseBadge.tsx
 *
 * Rôle :
 *   Pastille discrète à côté du brand TNE 2026 dans le Header. Affiche
 *   l'état temporel de l'événement à l'utilisateur :
 *     - 'before' : « J-N » (corail) — incite à venir le 27 mai.
 *     - 'during' : « LIVE » (vert pulsant) — événement en cours.
 *     - 'after'  : « Replays » (rouge brique) — bascule rétrospective.
 *
 *   Sous-composant isolé pour ne PAS faire re-render le Header complet
 *   chaque minute (le hook useEventPhase tique régulièrement).
 *
 * Réutilisable :
 *   100 %. Pour une autre app DRANE, garder la signature et changer
 *   la palette via le module CSS. Le hook useEventPhase est paramétrable.
 *
 * Voir CLAUDE.md §F12 (mode avant/pendant/après).
 *
 * @author Mika
 * @since 2026-05-06 (S19-B)
 */

import { FormattedMessage, useIntl } from 'react-intl';
import { useEventPhase } from '@/hooks/useEventPhase';
import styles from './Header.module.css';

/**
 * Pastille phase de l'événement. Sans prop : lit le hook directement.
 * On l'isole pour limiter le re-render à ce composant léger uniquement.
 *
 * NOLAS 2026 (2026-06-23) — quand l'« Aperçu intégral admin » est actif
 * (`revealAll`), la phase est forcée à `during` : on ajoute un suffixe
 * « · aperçu » + un title pour que l'admin n'oublie pas qu'il regarde un
 * aperçu et non l'état réel.
 */
export function PhaseBadge() {
  const intl = useIntl();
  const { phase, daysToEvent, revealAll } = useEventPhase();

  const previewTitle = revealAll
    ? intl.formatMessage({ id: 'header.phase.previewHint' })
    : undefined;
  const previewSuffix = revealAll ? (
    <span className={styles.phasePreviewSuffix}> · <FormattedMessage id="header.phase.preview" /></span>
  ) : null;

  if (phase === 'before') {
    return (
      <span
        className={`${styles.phaseBadge} ${styles.phaseBadgeBefore}`}
        // Annoncé poliment aux lecteurs d'écran sans interrompre l'utilisateur.
        aria-live="polite"
        data-phase="before"
      >
        <FormattedMessage
          id="header.phase.before"
          values={{ days: Math.max(daysToEvent, 0) }}
        />
      </span>
    );
  }

  if (phase === 'during') {
    return (
      <span
        className={`${styles.phaseBadge} ${styles.phaseBadgeDuring}`}
        aria-live="polite"
        data-phase="during"
        data-reveal={revealAll ? 'true' : undefined}
        title={previewTitle}
      >
        <span className={styles.phaseDot} aria-hidden="true" />
        <FormattedMessage id="header.phase.live" />
        {previewSuffix}
      </span>
    );
  }

  // phase === 'after'
  return (
    <span
      className={`${styles.phaseBadge} ${styles.phaseBadgeAfter}`}
      data-phase="after"
    >
      <FormattedMessage id="header.phase.replays" />
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Cliquer sur la pastille « LIVE » → ouvre un mini-popover « En cours :
//    quel atelier, quel stand, lien rapide ».
//  - Variante badge spécifique aux exposants (couleur différente).
// Notes pour rétro-port NOLAS :
//  - Pattern parfait pour signaler l'état d'un salon NOLAS sur la home.
//    Suffit de fournir un useEventPhase paramétré sur ses dates.
// ──────────────────────────────────────────────────────────────────────────
